/**
 * Sign with a certificate (a digital ID): the legally stronger kind of
 * signature, which proves who signed and that nothing changed afterwards.
 *
 * The certificate file (.pfx / .p12) and its password go to the Odie server
 * for this one request, over HTTPS, and are never stored.
 */

import { useState } from 'react'
import { api, type SessionInfo, type SignatureReport } from '../api'

type Placement = 'box' | 'invisible' | `field:${string}`

interface Props {
  sessionId: string
  emptyFields: SignatureReport['emptyFields']
  /** The box drawn on the page, once there is one. */
  box: { pageIndex: number; rect: [number, number, number, number] } | null
  /** Hidden while the user draws the box, so what they typed survives. */
  hidden: boolean
  onDrawBox: () => void
  onSigned: (report: SignatureReport & { session: SessionInfo }) => void
  onClose: () => void
}

export function CertificateSignDialog(props: Props) {
  const { sessionId, emptyFields, box } = props
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [who, setWho] = useState<{ signer: string; issuer: string; validTo: string } | null>(null)
  const [reason, setReason] = useState('')
  const [location, setLocation] = useState('Swift Current, SK')
  const [placement, setPlacement] = useState<Placement>(
    emptyFields.length ? `field:${emptyFields[0].name}` : 'box')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const check = async () => {
    if (!file) return
    setError('')
    setWho(null)
    try {
      setWho(await api.checkCertificate(sessionId, file, password))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const sign = async () => {
    if (!file) return
    setBusy(true)
    setError('')
    try {
      const field = placement.startsWith('field:') ? placement.slice(6) : undefined
      const r = await api.sign(sessionId, file, {
        password, reason, location,
        fieldName: field,
        pageIndex: placement === 'box' && box ? box.pageIndex : 0,
        rect: placement === 'box' && box ? box.rect : null,
      })
      props.onSigned(r)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const ready = Boolean(file && who && (placement !== 'box' || box))
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="csign-title"
         style={props.hidden ? { display: 'none' } : undefined}>
      <form className="modal__panel" onSubmit={(e) => { e.preventDefault(); void sign() }}>
        <h2 className="modal__title" id="csign-title">Sign with a certificate</h2>
        <div className="modal__body">
          <div className="field-row">
            <label className="field">
              <span>Certificate (.pfx or .p12)</span>
              <input type="file" accept=".pfx,.p12,application/x-pkcs12"
                     onChange={(e) => { setFile(e.target.files?.[0] ?? null); setWho(null) }} />
            </label>
            <label className="field">
              <span>Its password</span>
              <input type="password" autoComplete="off" value={password}
                     onChange={(e) => { setPassword(e.target.value); setWho(null) }}
                     onBlur={() => void check()} />
            </label>
          </div>
          {file && !who && !error && (
            <button type="button" className="btn btn--small" onClick={() => void check()}>Check certificate</button>
          )}
          {who && (
            <p className="csign__who">
              Signing as <strong>{who.signer}</strong>, issued by {who.issuer}, valid until {who.validTo}.
            </p>
          )}

          <div className="field-row">
            <label className="field">
              <span>Reason (optional)</span>
              <input value={reason} placeholder="I approve this document" onChange={(e) => setReason(e.target.value)} />
            </label>
            <label className="field">
              <span>Location (optional)</span>
              <input value={location} onChange={(e) => setLocation(e.target.value)} />
            </label>
          </div>

          <fieldset className="csign__where">
            <legend>Where the signature shows</legend>
            {emptyFields.map((f) => (
              <label className="check" key={f.name}>
                <input type="radio" name="csign-where" checked={placement === `field:${f.name}`}
                       onChange={() => setPlacement(`field:${f.name}`)} />
                In the form&rsquo;s signature field &ldquo;{f.name}&rdquo;
                {f.pageIndex !== null ? ` (page ${f.pageIndex + 1})` : ''}
              </label>
            ))}
            <label className="check">
              <input type="radio" name="csign-where" checked={placement === 'box'}
                     onChange={() => setPlacement('box')} />
              In a box I draw on the page
              {box ? ` — page ${box.pageIndex + 1}` : ''}
              <button type="button" className="btn btn--small" style={{ marginLeft: 8 }}
                      onClick={() => { setPlacement('box'); props.onDrawBox() }}>
                {box ? 'Redraw' : 'Draw box'}
              </button>
            </label>
            <label className="check">
              <input type="radio" name="csign-where" checked={placement === 'invisible'}
                     onChange={() => setPlacement('invisible')} />
              Invisible (listed in the Signatures panel only)
            </label>
          </fieldset>

          <p className="modal__hint">
            Signing seals the document as it is now: any later change shows as a
            change after signing. Save straight after signing. The certificate and
            its password are used for this signature only, and not kept.
          </p>
          {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        </div>
        <div className="modal__footer">
          <button type="button" className="btn" onClick={props.onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!ready || busy}
                  title={!who ? 'Choose a certificate and enter its password' : !ready ? 'Draw the signature box first' : undefined}>
            {busy ? 'Signing…' : 'Sign'}
          </button>
        </div>
      </form>
    </div>
  )
}
